import { API_VERSION } from 'shared/consts'
import { Context } from 'koa'
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import curlirize from 'axios-curlirize/dist/curlirize'
import assert from 'shared/utils/assert'
import BackendError from 'shared/errors/BackendError'
import TimeoutError from 'shared/errors/TimeoutError'
import { pick } from 'shared/utils/objectUtils'
import loggerService, { Logger as ServerLogger } from 'shared/services/logger'
import { Logger as ClientLogger } from 'console-log-level'
import { BULLY_AUTH_HEADER_NAME } from 'shared/consts/authCookie'
import refreshAuthCookie from 'shared/utils/refreshAuthCookie'
import { X_FEATURE } from 'shared/consts/httpHeaders'
import deepmerge from 'deepmerge'
import { ServiceParameters } from './commonTypes/ServiceParameters'
import { Headers } from './commonTypes/IService'

const stringifyRequest: (
  request: AxiosRequestConfig | AxiosResponse
) => string = (request: AxiosRequestConfig | AxiosResponse): string =>
  JSON.stringify(
    pick(request, ['headers', 'method', 'baseURL', 'url', 'params', 'data']),
    null,
    2
  )

abstract class APIService {
  protected baseURL: string
  protected apiVersion: string
  protected featureBranch: string
  protected client: AxiosInstance
  protected logger: ServerLogger | ClientLogger

  constructor({
    apiVersion = API_VERSION,
    authToken,
    baseURL,
    locale,
    contentTypeJSON = false,
    featureBranch,
    logger = loggerService,
    needsAuth,
    responseInterceptor: customResponseInterceptor = null
  }: ServiceParameters) {
    assert(baseURL, 'APIService: no "baseURL" option provided')
    assert(logger, 'APIService: no "logger" option provided')
    const params: AxiosRequestConfig = {
      baseURL,
      timeout: 15000
    }

    const headers: any = {}

    if (needsAuth && authToken) {
      headers.Authorization = authToken
    }

    if (locale) {
      headers['Accept-Language'] = locale
    }

    if (contentTypeJSON) {
      headers.Accept = 'application/json'
      headers['Content-Type'] = 'application/json'
    }

    if (Object.keys(headers).length > 0) {
      params.headers = headers
    }

    const client: AxiosInstance = axios.create(params)

    if (process.env.DEBUG_AXIOS === 'true') {
      curlirize(client)
    }

    this.baseURL = baseURL
    this.apiVersion = apiVersion
    this.client = client
    this.logger = logger

    if (featureBranch) {
      this.featureBranch = featureBranch
    }

    const handleRequest = (request: AxiosRequestConfig): AxiosRequestConfig => {
      if (process.env.APP_ENV === 'development') {
        logger.debug(stringifyRequest(request), 'api:request')
      }
      return request
    }

    const handleResponse = (response: AxiosResponse): AxiosResponse => {
      if (process.env.APP_ENV === 'development') {
        logger.debug(stringifyRequest(response), 'api:response')
      }
      return response
    }

    this.client.interceptors.request.use(handleRequest)
    this.client.interceptors.response.use(handleResponse, error => {
      const { message } = error
      const isTimoutExceeded = /\btimeout\b.*\bexceeded\b|\bexceeded\b.*\btimeout\b/gi.test(
        message
      )
      switch (true) {
        case isTimoutExceeded:
          throw new TimeoutError(error)
        default:
          throw new BackendError(error)
      }
    })

    if (typeof customResponseInterceptor === 'function') {
      this.client.interceptors.response.use(customResponseInterceptor)
    }
  }

  private async request<T>(
    config: AxiosRequestConfig,
    ctx: Context = null
  ): Promise<AxiosResponse<T>> {
    const result: AxiosResponse<T> = await this.client.request<T>(
      deepmerge(config, this.prepareConfigFromContext(ctx))
    )

    if (ctx) {
      refreshAuthCookie(ctx, result.headers?.[BULLY_AUTH_HEADER_NAME])
    }

    return result
  }

  private prepareConfigFromContext(ctx: Context): AxiosRequestConfig | {} {
    const headers: Headers = {}

    if (this.featureBranch) {
      headers[X_FEATURE] = this.featureBranch
    }

    const authorization = ctx?.state?.authToken
    if (authorization) {
      headers.Authorization = authorization
    }

    if (Object.keys(headers).length === 0) {
      return headers
    }

    return { headers }
  }

  // eslint-disable-next-line max-params
  protected async get<T>(
    path: string,
    options: AxiosRequestConfig = null,
    ctx: Context = null
  ): Promise<AxiosResponse<T>> {
    assert(path, 'APIService.get(): Please provide a path.')

    return this.request<T>(
      {
        method: 'GET',
        url: path,
        ...options
      },
      ctx
    )
  }

  // eslint-disable-next-line max-params
  protected async post<B, T>(
    path: string,
    payload: B,
    options: AxiosRequestConfig = null,
    ctx: Context = null
  ): Promise<AxiosResponse<T>> {
    assert(path, 'APIService.post(): Please provide a path.')

    return this.request(
      {
        method: 'POST',
        url: path,
        data: payload,
        ...options
      },
      ctx
    )
  }

  protected sendBeacon<P>(path: string, payload: P): void {
    assert(path, 'APIService.sendBeacon(): Please provide a path.')
    assert(payload, 'APIService.sendBeacon(): Please provide a payload.')

    const blob: Blob = new Blob([JSON.stringify(payload)], {
      type: 'application/json;charset=UTF-8'
    }) // the blob is used to add the content type
    if (!navigator.sendBeacon) return

    navigator.sendBeacon(this.baseURL + path, blob)
  }

  // eslint-disable-next-line max-params
  protected async put<P, T>(
    path: string,
    payload: P,
    options: AxiosRequestConfig = null,
    ctx: Context = null
  ): Promise<AxiosResponse<T>> {
    assert(path, 'APIService.put(): Please provide a path.')
    assert(payload, 'APIService.put(): Please provide a payload.')

    return this.request(
      {
        method: 'PUT',
        url: path,
        data: payload,
        ...options
      },
      ctx
    )
  }

  // eslint-disable-next-line max-params
  protected async patch<P, T>(
    path: string,
    payload: P,
    options: AxiosRequestConfig = null,
    ctx: Context = null
  ): Promise<AxiosResponse<T>> {
    assert(path, 'APIService.patch(): Please provide a path.')
    assert(payload, 'APIService.patch(): Please provide a payload.')

    return this.request(
      {
        method: 'PATCH',
        url: path,
        data: payload,
        ...options
      },
      ctx
    )
  }

  // eslint-disable-next-line max-params
  protected async delete(
    path: string,
    options: AxiosRequestConfig = null,
    ctx: Context = null
  ): Promise<AxiosResponse<void>> {
    assert(path, 'APIService.delete(): Please provide a path.')

    return this.request(
      {
        method: 'DELETE',
        url: path,
        ...options
      },
      ctx
    )
  }
}

export default APIService
